/**
 * HTML serialization utilities for the InputEditor component
 *
 * This file handles conversion between HTML strings and Slate's internal
 * document structure. It provides:
 * - HTML → Slate parsing (htmlToSlate)
 * - Slate → HTML serialization (slateToHtml)
 * - Support for text formatting, links, lists, alignment, and indentation
 */

import { Descendant } from 'slate';
import { CustomElement, CustomText } from './input-editor.types';

// ============================================================================
// HTML TO SLATE CONVERSION
// ============================================================================

/**
 * Parse text content and extract formatting information (bold, italic)
 *
 * @param text - Raw HTML text that may contain formatting tags
 * @returns CustomText object with formatting properties
 */
const parseTextWithFormatting = (text: string): CustomText => {
  // Extract clean text content (remove HTML tags)
  const cleanText = text.replace(/<[^>]*>/g, '');
  const textObj: CustomText = { text: cleanText || '' };

  // Check for bold formatting
  if (/<strong>|<b>/i.test(text)) {
    textObj.bold = true;
  }

  // Check for italic formatting
  if (/<em>|<i>/i.test(text)) {
    textObj.italic = true;
  }

  return textObj;
};

/**
 * Parse HTML content and convert to Slate elements and text nodes
 *
 * This function handles:
 * - Link elements (<a> tags)
 * - Text formatting (bold, italic)
 * - Proper text node structure
 *
 * @param htmlContent - HTML string to parse
 * @returns Array of Slate elements and text nodes
 */
const parseHtmlContent = (
  htmlContent: string,
): (CustomElement | CustomText)[] => {
  // Handle empty content
  if (!htmlContent.trim()) {
    return [{ text: '' }];
  }

  // Regex to match link elements with URL and content
  const linkRegex = /<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/g;
  const parts: (CustomElement | CustomText)[] = [];
  let lastIndex = 0;
  let match;

  // Reset regex state for reliable matching
  linkRegex.lastIndex = 0;

  // Process each link found in the content
  while ((match = linkRegex.exec(htmlContent)) !== null) {
    const [fullMatch, url, linkText] = match;
    const startIndex = match.index;

    // Add any text content before this link
    if (startIndex > lastIndex) {
      const beforeText = htmlContent.slice(lastIndex, startIndex);
      parts.push(parseTextWithFormatting(beforeText));
    }

    // Add the link element (ensure it has valid content)
    const cleanLinkText = linkText.replace(/<[^>]*>/g, '');
    if (cleanLinkText.trim()) {
      parts.push({
        type: 'link',
        url: url || '#', // Fallback URL if none provided
        children: [parseTextWithFormatting(linkText)],
      } as CustomElement);
    }

    lastIndex = startIndex + fullMatch.length;
  }

  // Add any remaining text after the last link
  if (lastIndex < htmlContent.length) {
    const remainingText = htmlContent.slice(lastIndex);
    parts.push(parseTextWithFormatting(remainingText));
  }

  // If no links were found, parse as plain formatted text
  if (parts.length === 0) {
    parts.push(parseTextWithFormatting(htmlContent));
  }

  // Ensure we always return at least an empty text node
  return parts.length > 0 ? parts : [{ text: '' }];
};

/**
 * Convert HTML string to Slate document structure
 *
 * This is the main entry point for HTML parsing. It handles:
 * - Multiple paragraphs (preserves line breaks)
 * - Simple list detection (basic implementation)
 * - Fallback to single paragraph for malformed HTML
 *
 * @param html - HTML string to convert
 * @returns Array of Slate descendants (document structure)
 */
export const htmlToSlate = (html: string): Descendant[] => {
  // Handle empty input
  if (!html) {
    return [{ type: 'paragraph', children: [{ text: '' }] }];
  }

  // Basic list detection (simplified implementation)
  // TODO: Implement more robust list parsing
  if (html.includes('<ul>')) {
    return [
      {
        type: 'bulleted-list',
        children: [
          {
            type: 'list-item',
            children: [{ text: 'List item' }],
          } as CustomElement,
        ],
      } as CustomElement,
    ];
  }

  if (html.includes('<ol>')) {
    return [
      {
        type: 'numbered-list',
        children: [
          {
            type: 'list-item',
            children: [{ text: 'List item' }],
          } as CustomElement,
        ],
      } as CustomElement,
    ];
  }

  // Parse multiple paragraphs to preserve line breaks
  const paragraphRegex = /<p[^>]*>(.*?)<\/p>/gi;
  const paragraphs: string[] = [];

  // Extract all paragraph contents using matchAll for reliability
  const matches = html.matchAll(paragraphRegex);
  for (const match of matches) {
    paragraphs.push(match[1]);
  }

  // If no paragraph tags found, treat entire content as single paragraph
  if (paragraphs.length === 0) {
    const children = parseHtmlContent(html);
    return [{ type: 'paragraph', children } as CustomElement];
  }

  // Convert each paragraph to a separate Slate element
  const elements: CustomElement[] = [];

  for (const paragraphContent of paragraphs) {
    // Validate paragraph content before processing
    if (paragraphContent !== undefined && paragraphContent !== null) {
      const children = parseHtmlContent(paragraphContent);

      // Only add paragraphs with valid children
      if (children && children.length > 0) {
        elements.push({ type: 'paragraph', children } as CustomElement);
      }
    }
  }

  // Ensure we always return at least one paragraph
  return elements.length > 0
    ? elements
    : [{ type: 'paragraph', children: [{ text: '' }] }];
};

// ============================================================================
// SLATE TO HTML CONVERSION
// ============================================================================

/**
 * Convert Slate document structure to HTML string
 *
 * This function serializes the Slate document tree into HTML, handling:
 * - Text formatting (bold, italic)
 * - Block elements (paragraphs, headings, lists)
 * - Inline elements (links)
 * - Styling (alignment, indentation)
 *
 * @param value - Slate document structure to serialize
 * @returns HTML string representation
 */
export const slateToHtml = (value: Descendant[]): string => {
  /**
   * Recursively serialize a single node to HTML
   *
   * @param node - Slate node (element or text) to serialize
   * @returns HTML string for this node
   */
  const serialize = (node: any): string => {
    // Handle text nodes with formatting
    if ('text' in node) {
      let html = node.text;

      // Apply text formatting
      if (node.bold) html = `<strong>${html}</strong>`;
      if (node.italic) html = `<em>${html}</em>`;

      return html;
    }

    // Handle element nodes
    const children = node.children.map((n: any) => serialize(n)).join('');

    // Build inline styles for alignment and indentation
    const styles: string[] = [];
    if (node.align) {
      styles.push(`text-align: ${node.align}`);
    }
    if (node.indent && node.indent > 0) {
      // Each indent level = 20px margin
      styles.push(`margin-left: ${node.indent * 20}px`);
    }

    const styleAttr = styles.length > 0 ? ` style="${styles.join('; ')}"` : '';

    // Convert element type to appropriate HTML tag
    switch (node.type) {
      case 'bulleted-list':
        return `<ul${styleAttr}>${children}</ul>`;
      case 'numbered-list':
        return `<ol${styleAttr}>${children}</ol>`;
      case 'list-item':
        return `<li${styleAttr}>${children}</li>`;
      case 'heading':
        return `<h2${styleAttr}>${children}</h2>`;
      case 'link':
        return `<a href="${node.url}" target="_blank" rel="noopener noreferrer">${children}</a>`;
      default:
        // Default to paragraph for unknown types
        return `<p${styleAttr}>${children}</p>`;
    }
  };

  // Serialize all top-level nodes and join them
  return value.map(serialize).join('');
};
